/**
 * DesktopContactList — Slack-style collapsible sections for desktop two-pane messaging.
 *
 * Three sections:
 * - Direct Messages — DMs + group conversations (sorted by lastMessageAt)
 * - Channels — from useChannels(), filtered by search
 * - Starred Messages — global starred messages across all conversations
 *
 * Each section has a collapsible header with chevron, unread badge, and "+" button.
 * Navigation uses messagingNavBridge (outside Stackflow provider).
 */

import { useState, useMemo, useCallback, useEffect, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';

import { useAuthStore } from '@/features/auth';
import {
  useConversations,
  useChannels,
  useGlobalStarredMessages,
  getConversationDisplayName,
  getConversationAvatar,
} from '../hooks';
import { useBatchPresence, useConversationTyping } from '../hooks';
import { getMessagingFlow, peekPendingChat, onPendingChatFlushed } from '../messagingNavBridge';
import { messagingApi } from '../api';
import { ChatAvatar } from '../components';
import type { Conversation, Channel } from '../types';

// =============================================================================
// Section Header — Collapsible with chevron, unread badge, and "+" button
// =============================================================================

interface SectionHeaderProps {
  label: string;
  expanded: boolean;
  onToggle: () => void;
  unreadCount: number;
  onAdd: () => void;
}

function SectionHeader({ label, expanded, onToggle, unreadCount, onAdd }: SectionHeaderProps) {
  return (
    <div className="mx-3 mt-3 mb-1 flex items-center gap-1.5">
      <button
        type="button"
        onClick={onToggle}
        className="flex flex-1 items-center gap-1.5 rounded-md px-1.5 py-1 text-start transition-colors hover:bg-white/40"
      >
        <motion.svg
          animate={{ rotate: expanded ? 0 : -90 }}
          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          className="h-3.5 w-3.5 flex-shrink-0"
          style={{ color: '#6b7fa3' }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </motion.svg>
        <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#1a4fa0' }}>
          {label}
        </span>
        {unreadCount > 0 && (
          <span
            className="flex h-4.5 min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10px] font-medium text-white"
            style={{ background: 'linear-gradient(135deg, #1a4fa0, #3b8dd4)' }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      <button
        type="button"
        onClick={onAdd}
        className="flex flex-shrink-0 items-center justify-center transition-all hover:scale-105 active:scale-95"
        style={{
          width: 32,
          height: 32,
          borderRadius: 10,
          background: 'rgba(26, 79, 160, 0.08)',
          border: '1px solid rgba(26, 79, 160, 0.12)',
        }}
      >
        <svg className="h-3.5 w-3.5" style={{ color: '#1a4fa0' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </button>
    </div>
  );
}

// =============================================================================
// Section Header (no add button) — For sections like Starred
// =============================================================================

interface SectionHeaderSimpleProps {
  label: string;
  expanded: boolean;
  onToggle: () => void;
  count: number;
  icon?: React.ReactNode;
}

function SectionHeaderSimple({ label, expanded, onToggle, count, icon }: SectionHeaderSimpleProps) {
  return (
    <div className="mx-3 mt-3 mb-1 flex items-center gap-1.5">
      <button
        type="button"
        onClick={onToggle}
        className="flex flex-1 items-center gap-1.5 rounded-md px-1.5 py-1 text-start transition-colors hover:bg-white/40"
      >
        <motion.svg
          animate={{ rotate: expanded ? 0 : -90 }}
          transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          className="h-3.5 w-3.5 flex-shrink-0"
          style={{ color: '#6b7fa3' }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </motion.svg>
        {icon}
        <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#1a4fa0' }}>
          {label}
        </span>
        {count > 0 && (
          <span className="flex h-4.5 min-w-[18px] items-center justify-center rounded-full bg-yellow-100 px-1.5 text-[10px] font-bold text-yellow-700">
            {count}
          </span>
        )}
      </button>
    </div>
  );
}

// =============================================================================
// StarredRow — Starred message preview row
// =============================================================================

interface StarredRowProps {
  message: {
    id: string;
    conversationId: string;
    content?: string | null;
    type: string;
    createdAt: string;
    sender: { name: string; avatar?: string };
    conversation: { name: string };
  };
  onClick: () => void;
}

const StarredRow = memo(function StarredRow({ message: msg, onClick }: StarredRowProps) {
  const { i18n } = useTranslation();

  const timeLabel = (() => {
    const date = new Date(msg.createdAt);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const isHe = i18n.language === 'he';

    if (diff < 24 * 60 * 60 * 1000) {
      return date.toLocaleTimeString(isHe ? 'he-IL' : 'en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    }
    if (diff < 7 * 24 * 60 * 60 * 1000) {
      return date.toLocaleDateString(isHe ? 'he-IL' : 'en-US', { weekday: 'short' });
    }
    return date.toLocaleDateString(isHe ? 'he-IL' : 'en-US', {
      month: 'short',
      day: 'numeric',
    });
  })();

  return (
    <motion.button
      layout
      type="button"
      onClick={onClick}
      whileHover={{ scale: 1.02, x: 2 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      className="dock-icon-glass relative mx-3 my-1 flex w-[calc(100%-1.5rem)] items-center gap-3 p-3 text-start transition-all duration-200 hover:bg-white/50"
      style={{ borderRadius: 20 }}
    >
      <div className="flex-shrink-0">
        <ChatAvatar
          src={msg.sender.avatar}
          name={msg.sender.name}
          size="h-10 w-10"
          textSize="text-sm"
          borderColor="border-slate-200"
          fallbackClass="bg-gradient-to-br from-yellow-500 to-amber-300 text-white"
        />
      </div>
      <div className="flex-1 overflow-hidden" dir="auto">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-sm font-medium" style={{ color: '#0d2b6b' }}>
            {msg.sender.name}
          </span>
          <span className="flex-shrink-0 text-[10px] font-medium uppercase tracking-widest tabular-nums" style={{ color: '#6b7fa3' }}>
            {timeLabel}
          </span>
        </div>
        <p className="mt-0.5 truncate text-xs" style={{ color: '#6b7fa3' }} dir="auto">
          {msg.content || `[${msg.type}]`}
        </p>
        <div className="mt-0.5 flex items-center gap-1">
          <svg className="h-3 w-3" style={{ color: '#6b7fa3' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          </svg>
          <span className="truncate text-[11px]" style={{ color: '#6b7fa3' }}>{msg.conversation.name}</span>
        </div>
      </div>
      <svg className="h-3.5 w-3.5 flex-shrink-0 text-yellow-400" viewBox="0 0 24 24" fill="currentColor" stroke="none">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    </motion.button>
  );
});

// =============================================================================
// ContactRow — DM / Group conversation card
// =============================================================================

interface ContactRowProps {
  conversation: Conversation;
  isOnline: boolean;
  isActive: boolean;
  currentUserId: string;
  onClick: () => void;
}

const ContactRow = memo(function ContactRow({
  conversation: conv,
  isOnline,
  isActive,
  currentUserId,
  onClick,
}: ContactRowProps) {
  const { t, i18n } = useTranslation();
  const typingUsers = useConversationTyping(conv.id);
  const name = getConversationDisplayName(conv, currentUserId);
  const avatar = getConversationAvatar(conv, currentUserId);
  const hasUnread = conv.unreadCount > 0;

  const timeLabel = (() => {
    if (!conv.lastMessageAt) return '';
    const date = new Date(conv.lastMessageAt);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const isHe = i18n.language === 'he';

    if (diff < 24 * 60 * 60 * 1000) {
      return date.toLocaleTimeString(isHe ? 'he-IL' : 'en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    }
    if (diff < 7 * 24 * 60 * 60 * 1000) {
      return date.toLocaleDateString(isHe ? 'he-IL' : 'en-US', { weekday: 'short' });
    }
    return date.toLocaleDateString(isHe ? 'he-IL' : 'en-US', {
      month: 'short',
      day: 'numeric',
    });
  })();

  const lastMessagePreview = (() => {
    if (!conv.lastMessage) return '';
    const lm = conv.lastMessage;
    if (lm.type !== 'text') return `[${lm.type}]`;
    return lm.content || '';
  })();

  return (
    <motion.button
      layout
      type="button"
      onClick={onClick}
      whileHover={{ scale: 1.01, x: 2 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      className={`dock-icon-glass relative mx-3 my-1 flex w-[calc(100%-1.5rem)] items-center gap-3 p-3 text-start transition-all duration-200 ${
        isActive ? '' : 'hover:bg-white/50'
      }`}
      style={{
        borderRadius: 20,
        ...(isActive
          ? {
              background: 'rgba(26, 79, 160, 0.08)',
              border: '1px solid rgba(26, 79, 160, 0.15)',
            }
          : {}),
      }}
    >
      {isActive && (
        <motion.div
          layoutId="activeIndicator"
          className="absolute start-0 top-3 bottom-3 w-1 rounded-e-full shadow-sm"
          style={{ background: 'linear-gradient(180deg, #1a4fa0, #3b8dd4)' }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        />
      )}

      <div className={`relative flex-shrink-0 ${isOnline ? 'animate-online-glow rounded-full' : ''}`}>
        <ChatAvatar
          src={avatar}
          name={conv.type === 'channel' ? '#' : name}
          size="h-12 w-12"
          textSize="text-lg"
          borderColor="border-slate-200"
          fallbackClass="bg-gradient-to-br from-slate-600 to-slate-300 text-white"
        />
        {isOnline && (
          <div className="absolute -bottom-0.5 -end-0.5 h-3 w-3 rounded-full border border-white/20 bg-success-400" />
        )}
      </div>

      <div className="flex-1 overflow-hidden" dir="auto">
        <div className="flex items-center justify-between gap-2">
          <span
            className={`truncate text-sm ${hasUnread ? 'font-semibold' : 'font-medium'}`}
            style={{ color: '#0d2b6b' }}
          >
            {name}
          </span>
          <span
            className="flex-shrink-0 text-[10px] font-medium uppercase tracking-widest tabular-nums"
            style={{ color: hasUnread ? '#1a4fa0' : '#6b7fa3' }}
          >
            {timeLabel}
          </span>
        </div>
        <div className="mt-0.5 flex items-center justify-between gap-2">
          {typingUsers.length > 0 ? (
            <span className="flex items-center gap-1 truncate text-xs font-medium text-emerald-600">
              <span className="flex items-center gap-0.5">
                <span className="inline-block h-1 w-1 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '0ms', animationDuration: '600ms' }} />
                <span className="inline-block h-1 w-1 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '150ms', animationDuration: '600ms' }} />
                <span className="inline-block h-1 w-1 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '300ms', animationDuration: '600ms' }} />
              </span>
              {typingUsers.length === 1
                ? t('messaging.typingSingle', { name: typingUsers[0].userName })
                : t('messaging.typingMultiple', { count: typingUsers.length })}
            </span>
          ) : (
            <span className={`truncate text-xs ${hasUnread ? 'font-medium' : ''}`} style={{ color: hasUnread ? '#1a4fa0' : '#6b7fa3' }}>
              {conv.lastMessage && conv.type !== 'dm' && (
                <span className="font-medium" style={{ color: '#1a4fa0' }}>{conv.lastMessage.senderName}: </span>
              )}
              {lastMessagePreview}
            </span>
          )}
          {hasUnread && (
            <span
              className="animate-unread-pulse flex h-5 min-w-[20px] flex-shrink-0 items-center justify-center rounded-full px-1.5 text-[10px] font-medium text-white"
              style={{ background: 'linear-gradient(135deg, #1a4fa0, #3b8dd4)' }}
            >
              {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
            </span>
          )}
        </div>
      </div>
    </motion.button>
  );
});

// =============================================================================
// ChannelRow — Channel card with # avatar
// =============================================================================

interface ChannelRowProps {
  channel: Channel;
  isActive: boolean;
  onClick: () => void;
}

const ChannelRow = memo(function ChannelRow({ channel, isActive, onClick }: ChannelRowProps) {
  const { t, i18n } = useTranslation();
  const typingUsers = useConversationTyping(channel.id);

  const timeLabel = (() => {
    if (!channel.lastMessageAt) return '';
    const date = new Date(channel.lastMessageAt);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const isHe = i18n.language === 'he';

    if (diff < 24 * 60 * 60 * 1000) {
      return date.toLocaleTimeString(isHe ? 'he-IL' : 'en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    }
    if (diff < 7 * 24 * 60 * 60 * 1000) {
      return date.toLocaleDateString(isHe ? 'he-IL' : 'en-US', { weekday: 'short' });
    }
    return date.toLocaleDateString(isHe ? 'he-IL' : 'en-US', {
      month: 'short',
      day: 'numeric',
    });
  })();

  const subtitle = channel.description || t('messaging.members', { count: channel.memberCount });

  return (
    <motion.button
      layout
      type="button"
      onClick={onClick}
      whileHover={{ scale: 1.01, x: 2 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      className={`dock-icon-glass relative mx-3 my-1 flex w-[calc(100%-1.5rem)] items-center gap-3 p-3 text-start transition-all duration-200 ${
        isActive ? '' : 'hover:bg-white/50'
      }`}
      style={{
        borderRadius: 20,
        ...(isActive
          ? {
              background: 'rgba(26, 79, 160, 0.08)',
              border: '1px solid rgba(26, 79, 160, 0.15)',
            }
          : {}),
      }}
    >
      {isActive && (
        <motion.div
          layoutId="activeIndicator"
          className="absolute start-0 top-3 bottom-3 w-1 rounded-e-full shadow-sm"
          style={{ background: 'linear-gradient(180deg, #1a4fa0, #3b8dd4)' }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        />
      )}

      {/* # avatar */}
      <div
        className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full"
        style={{ background: 'rgba(26, 79, 160, 0.08)', border: '1px solid rgba(26, 79, 160, 0.1)' }}
      >
        <span className="text-lg font-semibold" style={{ color: '#1a4fa0' }}>#</span>
      </div>

      <div className="flex-1 overflow-hidden" dir="auto">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 overflow-hidden">
            <span className="truncate text-sm font-medium" style={{ color: '#0d2b6b' }}>
              {channel.name}
            </span>
            {channel.isJoined && (
              <span
                className="flex-shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium"
                style={{ background: 'rgba(26, 79, 160, 0.08)', color: '#1a4fa0' }}
              >
                {t('messaging.joined')}
              </span>
            )}
          </div>
          <span className="flex-shrink-0 text-[10px] font-medium uppercase tracking-widest tabular-nums" style={{ color: '#6b7fa3' }}>
            {timeLabel}
          </span>
        </div>
        <div className="mt-0.5">
          {typingUsers.length > 0 ? (
            <span className="flex items-center gap-1 truncate text-xs font-medium text-emerald-600">
              <span className="flex items-center gap-0.5">
                <span className="inline-block h-1 w-1 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '0ms', animationDuration: '600ms' }} />
                <span className="inline-block h-1 w-1 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '150ms', animationDuration: '600ms' }} />
                <span className="inline-block h-1 w-1 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '300ms', animationDuration: '600ms' }} />
              </span>
              {typingUsers.length === 1
                ? t('messaging.typingSingle', { name: typingUsers[0].userName })
                : t('messaging.typingMultiple', { count: typingUsers.length })}
            </span>
          ) : (
            <span className="truncate text-xs" style={{ color: '#6b7fa3' }}>{subtitle}</span>
          )}
        </div>
      </div>
    </motion.button>
  );
});

// =============================================================================
// Main Component
// =============================================================================

function getInitialConversationId(): string | null {
  const pending = peekPendingChat();
  if (pending) return pending;
  const match = window.location.pathname.match(/\/messages\/(.+)/);
  if (!match) return null;
  const id = match[1];
  if (id === 'new' || id === 'new-channel') return null;
  return id;
}

export function DesktopContactList() {
  const { t } = useTranslation();
  const currentUserId = useAuthStore((s) => s.user?.id) || '';

  const [searchQuery, setSearchQuery] = useState('');
  const [activeConversationId, setActiveConversationId] = useState<string | null>(getInitialConversationId);
  // True when a non-chat activity (NewConversation, NewChannel) occupies the right pane.
  // Used so clicking a contact replaces (not pushes on top of) the current activity.
  const [hasNonChatActivity, setHasNonChatActivity] = useState(false);
  const [dmExpanded, setDmExpanded] = useState(true);
  const [channelsExpanded, setChannelsExpanded] = useState(true);
  const [starredExpanded, setStarredExpanded] = useState(false);

  // Sync highlight when a pending deep-link is flushed by the bridge
  useEffect(() => {
    return onPendingChatFlushed(() => {
      const match = window.location.pathname.match(/\/messages\/(.+)/);
      if (match && match[1] !== 'new' && match[1] !== 'new-channel') {
        setActiveConversationId(match[1]);
      }
    });
  }, []);

  // Data sources
  const { data: conversations } = useConversations();
  const { data: channels } = useChannels();
  const { data: starredMessages } = useGlobalStarredMessages();

  // Batch presence for DM participants
  const allUserIds = useMemo(() => {
    const ids = new Set<string>();
    conversations?.forEach((c) => {
      c.participants.forEach((p) => {
        if (p.userId !== currentUserId) ids.add(p.userId);
      });
    });
    return Array.from(ids);
  }, [conversations, currentUserId]);

  const { presenceMap } = useBatchPresence(allUserIds);

  // DM items — conversations that are not channels, sorted by lastMessageAt
  const dmItems = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    const convList = (conversations || []).filter((c) => c.type !== 'channel');

    const filtered = convList.filter((c) => {
      if (!q) return true;
      const name = getConversationDisplayName(c, currentUserId).toLowerCase();
      return name.includes(q);
    });

    // Sort: conversations with messages first (desc by time), then empty ones
    return filtered.sort((a, b) => {
      const aTime = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
      const bTime = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
      if (aTime === 0 && bTime === 0) return 0;
      if (aTime === 0) return 1;
      if (bTime === 0) return -1;
      return bTime - aTime;
    });
  }, [conversations, searchQuery, currentUserId]);

  // Filtered channels from useChannels()
  const filteredChannels = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!channels) return [];
    if (!q) return channels;
    return channels.filter(
      (ch) =>
        ch.name.toLowerCase().includes(q) ||
        (ch.description && ch.description.toLowerCase().includes(q)),
    );
  }, [channels, searchQuery]);

  // Unread counts for section headers
  const dmUnreadCount = useMemo(
    () =>
      conversations
        ?.filter((c) => c.type !== 'channel')
        .reduce((s, c) => s + c.unreadCount, 0) ?? 0,
    [conversations],
  );
  const channelUnreadCount = useMemo(
    () =>
      conversations
        ?.filter((c) => c.type === 'channel')
        .reduce((s, c) => s + c.unreadCount, 0) ?? 0,
    [conversations],
  );

  // Navigation handlers
  const handleConversationClick = useCallback(
    (conversationId: string) => {
      const flow = getMessagingFlow();
      if (!flow) return;
      // Replace when any activity already occupies the right pane
      // (a chat, NewConversation, or NewChannel)
      if (activeConversationId || hasNonChatActivity) {
        flow.replace('ChatActivity', { conversationId });
      } else {
        flow.push('ChatActivity', { conversationId });
      }
      setActiveConversationId(conversationId);
      setHasNonChatActivity(false);
    },
    [activeConversationId, hasNonChatActivity],
  );

  const handleChannelClick = useCallback(
    async (channelId: string) => {
      // Auto-join if not yet a member
      const ch = filteredChannels.find((c) => c.id === channelId);
      if (ch && !ch.isJoined) {
        try { await messagingApi.joinChannel(channelId); } catch { /* ignore */ }
      }
      const flow = getMessagingFlow();
      if (!flow) return;
      if (activeConversationId || hasNonChatActivity) {
        flow.replace('ChatActivity', { conversationId: channelId });
      } else {
        flow.push('ChatActivity', { conversationId: channelId });
      }
      setActiveConversationId(channelId);
      setHasNonChatActivity(false);
    },
    [activeConversationId, hasNonChatActivity, filteredChannels],
  );

  const handleNewDm = useCallback(() => {
    const flow = getMessagingFlow();
    if (!flow) return;
    if (activeConversationId || hasNonChatActivity) {
      flow.replace('NewConversationActivity', {});
    } else {
      flow.push('NewConversationActivity', {});
    }
    setActiveConversationId(null);
    setHasNonChatActivity(true);
  }, [activeConversationId, hasNonChatActivity]);

  const handleNewChannel = useCallback(() => {
    const flow = getMessagingFlow();
    if (!flow) return;
    if (activeConversationId || hasNonChatActivity) {
      flow.replace('NewChannelActivity', {});
    } else {
      flow.push('NewChannelActivity', {});
    }
    setActiveConversationId(null);
    setHasNonChatActivity(true);
  }, [activeConversationId, hasNonChatActivity]);

  const handleStarredClick = useCallback(
    (msg: { conversationId: string; id: string }) => {
      const flow = getMessagingFlow();
      if (!flow) return;
      if (activeConversationId || hasNonChatActivity) {
        flow.replace('ChatActivity', { conversationId: msg.conversationId });
      } else {
        flow.push('ChatActivity', { conversationId: msg.conversationId });
      }
      setActiveConversationId(msg.conversationId);
      setHasNonChatActivity(false);
      // Scroll to the message after navigation settles
      setTimeout(() => {
        const el = document.getElementById(`msg-${msg.id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    },
    [activeConversationId, hasNonChatActivity],
  );

  const hasAnyResults = dmItems.length > 0 || filteredChannels.length > 0;

  return (
    <>
      <style>{`.desktop-contacts-search::placeholder { color: rgba(13,43,107,0.4) !important; }`}</style>

      {/* Header — frosted glass */}
      <header
        className="flex flex-shrink-0 items-end px-6 pb-3"
        style={{
          height: 84,
          backgroundColor: 'rgba(255,255,255,0.55)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        }}
      >
        <h1 className="text-lg sm:text-2xl font-semibold tracking-wide" style={{ color: '#1e293b' }}>{t('messaging.title')}</h1>
      </header>

      {/* Search — frosted glass */}
      <div
        className="px-4 pt-3 pb-2"
        style={{
          backgroundColor: 'rgba(255,255,255,0.45)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderBottom: '2px solid rgba(255,255,255,0.25)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}
      >
        <div className="relative">
          <svg
            className="pointer-events-none absolute start-3.5 top-1/2 h-4 w-4 -translate-y-1/2"
            style={{ color: 'rgba(13,43,107,0.35)' }}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('messaging.searchContacts')}
            dir="auto"
            className="desktop-contacts-search w-full rounded-full py-2.5 pe-4 ps-10 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-white/30"
            style={{
              color: '#0d2b6b',
              background: 'rgba(255,255,255,0.35)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.4)',
            }}
          />
        </div>
      </div>

      {/* Sections */}
      <div
        className="custom-scrollbar flex-1 overflow-y-auto py-1"
        style={{
          backgroundColor: 'rgba(255,255,255,0.55)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        }}
      >
        {/* ── Direct Messages ── */}
        <SectionHeader
          label={t('messaging.directMessages')}
          expanded={dmExpanded}
          onToggle={() => setDmExpanded((v) => !v)}
          unreadCount={dmUnreadCount}
          onAdd={handleNewDm}
        />
        <AnimatePresence initial={false}>
          {dmExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className="overflow-hidden"
            >
              {dmItems.map((conv) => {
                const dmPartnerId =
                  conv.type === 'dm'
                    ? conv.participants.find((p) => p.userId !== currentUserId)?.userId
                    : null;
                return (
                  <ContactRow
                    key={`conv-${conv.id}`}
                    conversation={conv}
                    isOnline={dmPartnerId ? !!presenceMap[dmPartnerId]?.isOnline : false}
                    isActive={conv.id === activeConversationId}
                    currentUserId={currentUserId}
                    onClick={() => handleConversationClick(conv.id)}
                  />
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Channels ── */}
        <SectionHeader
          label={t('messaging.channels')}
          expanded={channelsExpanded}
          onToggle={() => setChannelsExpanded((v) => !v)}
          unreadCount={channelUnreadCount}
          onAdd={handleNewChannel}
        />
        <AnimatePresence initial={false}>
          {channelsExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className="overflow-hidden"
            >
              {filteredChannels.map((ch) => (
                <ChannelRow
                  key={`chan-${ch.id}`}
                  channel={ch}
                  isActive={ch.id === activeConversationId}
                  onClick={() => handleChannelClick(ch.id)}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Starred Messages ── */}
        {starredMessages && starredMessages.length > 0 && (
          <>
            <SectionHeaderSimple
              label={t('messaging.starredMessages')}
              expanded={starredExpanded}
              onToggle={() => setStarredExpanded((v) => !v)}
              count={starredMessages.length}
              icon={
                <svg className="h-3.5 w-3.5 flex-shrink-0 text-yellow-500" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              }
            />
            <AnimatePresence initial={false}>
              {starredExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  className="overflow-hidden"
                >
                  {(starredMessages as Array<{
                    id: string;
                    conversationId: string;
                    content?: string | null;
                    type: string;
                    createdAt: string;
                    sender: { name: string; avatar?: string };
                    conversation: { name: string };
                  }>).map((msg) => (
                    <StarredRow
                      key={`starred-${msg.id}`}
                      message={msg}
                      onClick={() => handleStarredClick(msg)}
                    />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        {/* Empty state */}
        {!hasAnyResults && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <svg className="mb-3 h-8 w-8" style={{ color: '#3b8dd4' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
            </svg>
            <p className="text-sm" style={{ color: '#6b7fa3' }}>
              {searchQuery ? t('messaging.noResults') : t('messaging.noConversations')}
            </p>
          </div>
        )}
      </div>
    </>
  );
}
